/**
 * Feedback API Client
 * Sprint 1, Task 11 & 12: Feedback Collection & Storage
 */

import { getAPIClient } from './client';

export interface FeedbackSubmission {
  query_id: string;
  project_id: string;
  feedback_type: 'helpful' | 'not_helpful' | 'report_issue';
  rating?: number;
  issue_category?: string;
  comment?: string;
  answer_relevance_score?: number;
  accuracy_score?: number;
  completeness_score?: number;
  citation_quality_score?: number;
  timestamp: string;
}

export interface FeedbackStats {
  total_feedback: number;
  helpful_count: number;
  not_helpful_count: number;
  avg_rating: number;
  helpfulness_rate: number;
  recent_issues: Array<{
    id: string;
    issue_category: string;
    comment: string;
    created_at: string;
  }>;
}

/**
 * Submit user feedback
 */
export async function submitFeedbackAPI(feedback: FeedbackSubmission): Promise<void> {
  const client = getAPIClient();
  
  try {
    await client.post('/feedback/submit', feedback);
  } catch (error: any) {
    console.error('Failed to submit feedback:', error);
    
    // Extract detailed error message from backend response
    let errorMessage = 'Feedback submission failed';
    if (error.response?.data) {
      const errorData = error.response.data;
      if (typeof errorData.detail === 'string') {
        errorMessage = errorData.detail;
      } else if (Array.isArray(errorData.detail)) {
        // Handle validation errors array
        errorMessage = errorData.detail.map((err: any) => 
          `${err.loc?.join('.') || 'field'}: ${err.msg || err.message || 'Invalid value'}`
        ).join(', ');
      } else if (errorData.detail) {
        errorMessage = JSON.stringify(errorData.detail);
      }
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    throw new Error(errorMessage);
  }
}

/**
 * Get feedback for a specific query
 */
export async function getQueryFeedback(queryId: string): Promise<FeedbackSubmission | null> {
  const client = getAPIClient();
  
  try {
    const response = await client.get(`/feedback/query/${queryId}`);
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    console.error('Failed to get query feedback:', error);
    throw error;
  }
}

/**
 * Get feedback statistics for a project
 */
export async function getProjectFeedbackStats(projectId: string): Promise<FeedbackStats> {
  const client = getAPIClient();
  
  try {
    const response = await client.get(`/feedback/project/${projectId}/stats`);
    return response.data;
  } catch (error: any) {
    console.error('Failed to get feedback stats:', error);
    throw new Error(`Failed to fetch feedback stats: ${error.response?.data?.detail || error.message}`);
  }
}

/**
 * Get recent issues for a project
 */
export async function getProjectIssues(
  projectId: string,
  limit: number = 20
): Promise<Array<any>> {
  const client = getAPIClient();
  
  try {
    const response = await client.get(`/feedback/project/${projectId}/issues`, {
      params: { limit }
    });
    return response.data;
  } catch (error: any) {
    console.error('Failed to get project issues:', error);
    throw error;
  }
}

/**
 * Mark a feedback issue as resolved
 */
export async function resolveFeedbackIssue(feedbackId: string): Promise<void> {
  const client = getAPIClient();
  
  try {
    await client.post(`/feedback/${feedbackId}/resolve`);
  } catch (error: any) {
    console.error('Failed to resolve feedback:', error);
    throw error;
  }
}

/**
 * Send feedback notification email to recipients.
 * When toEmail is provided (e.g. from HeaderFeedbackButton), only that address is used; otherwise default list is used.
 * Backend adds sign-off "Thanks, <username>" to the message.
 */
export interface FeedbackEmailNotificationRequest {
  subject: string;
  message: string;
  /** @deprecated No longer used; use sendFeedbackEmailWithAttachments for real file attachments */
  attachments?: string;
  feedbackId?: string;
  projectId?: string;
  /** Optional single recipient; when set, email is sent only to this address (e.g. header feedback) */
  toEmail?: string;
}

export interface FeedbackEmailNotificationResponse {
  success: boolean;
  message: string;
  sent_to?: string[];
}

// Hardcoded recipient email addresses
const FEEDBACK_RECIPIENT_EMAILS = [
  'parsa@sparcglobal.ca',
  'krish@sparcglobal.ca',
  'cshek@keystoneenvironmental.ca',
  'telkadri@keystoneenvironmental.ca'
];

export async function sendFeedbackEmailNotification(
  request: FeedbackEmailNotificationRequest
): Promise<FeedbackEmailNotificationResponse> {
  const toEmail =
    request.toEmail && request.toEmail.trim()
      ? request.toEmail.trim()
      : FEEDBACK_RECIPIENT_EMAILS.join(',');

  const client = getAPIClient();
  try {
    const data = await client.post<FeedbackEmailNotificationResponse>(
      '/feedback/send-email',
      {
        subject: request.subject,
        message: request.message,
        to_email: toEmail,
        feedback_id: request.feedbackId ?? undefined,
        project_id: request.projectId ?? undefined,
      }
    );
    return data as FeedbackEmailNotificationResponse;
  } catch (error: any) {
    console.error('Failed to send feedback email:', error);
    const detail = error.response?.data?.detail;
    const msg =
      typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map((e: any) => e.msg || e.message).join(', ')
          : error.message || 'Failed to send feedback email';
    throw new Error(msg);
  }
}

/**
 * Send feedback email with real file attachments (multipart).
 * Attachments are sent as email attachments; backend adds sign-off "Thanks, <username>".
 */
export async function sendFeedbackEmailWithAttachments(
  request: Omit<FeedbackEmailNotificationRequest, 'attachments'> & { files: File[] }
): Promise<FeedbackEmailNotificationResponse> {
  const toEmail =
    request.toEmail && request.toEmail.trim()
      ? request.toEmail.trim()
      : FEEDBACK_RECIPIENT_EMAILS.join(',');

  const client = getAPIClient();
  const formData = new FormData();
  formData.append('subject', request.subject);
  formData.append('message', request.message);
  formData.append('to_email', toEmail);
  if (request.feedbackId) formData.append('feedback_id', request.feedbackId);
  if (request.projectId) formData.append('project_id', request.projectId);
  request.files.forEach((file) => formData.append('attachments', file));

  try {
    // Do not set Content-Type; axios sets multipart/form-data with boundary when data is FormData
    const data = await client.post<FeedbackEmailNotificationResponse>(
      '/feedback/send-email-with-attachments',
      formData
    );
    return data as FeedbackEmailNotificationResponse;
  } catch (error: any) {
    console.error('Failed to send feedback email with attachments:', error);
    const detail = error.response?.data?.detail;
    const msg =
      typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map((e: any) => e.msg || e.message).join(', ')
          : error.message || 'Failed to send feedback email';
    throw new Error(msg);
  }
}

/**
 * Public help request from login page (no authentication).
 * Sends to administrator email (e.g. parsa@sparcglobal.ca).
 */
export interface HelpRequestPublicRequest {
  subject?: string;
  message: string;
}

export interface HelpRequestPublicResponse {
  success: boolean;
  message: string;
}

export async function sendHelpRequestPublic(
  request: HelpRequestPublicRequest
): Promise<HelpRequestPublicResponse> {
  const { getAPIBaseUrl } = await import('./client');
  const baseUrl = getAPIBaseUrl();
  const url = `${baseUrl}/feedback/help-request`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      subject: request.subject || undefined,
      message: request.message.trim(),
    }),
  });
  const data = (await res.json()) as HelpRequestPublicResponse & { detail?: string };
  if (!res.ok) {
    throw new Error(data.detail || data.message || 'Failed to send help request');
  }
  return data;
}

export default {
  submitFeedbackAPI,
  getQueryFeedback,
  getProjectFeedbackStats,
  getProjectIssues,
  resolveFeedbackIssue,
  sendFeedbackEmailNotification,
  sendFeedbackEmailWithAttachments,
  sendHelpRequestPublic,
};

